// shellUpdate.js — keep thin-client shells (iOS/Capacitor + desktop/Electron)
// from running a STALE web bundle after a new deploy.
//
// Both shells load the LIVE site (chatrone-os.vercel.app) rather than a bundled
// copy, but their webview can keep a backgrounded page — or a disk-cached
// index.html — around, so a shipped web fix can lay dormant until the user
// force-quits the app. A normal browser tab doesn't have this problem (any
// reload re-fetches index.html); the shells do, because the user rarely does a
// hard reload. That's exactly how the full-screen-Janet fix "looked the same"
// on the phone after it had already shipped.
//
// Detection is build-tool-agnostic: Vite injects a content-hashed entry script
// (<script src="/assets/index-HASH.js">) into index.html, and that hash changes
// on every deploy that ships new app code. We compare the hash the running
// document booted from against the one the live index.html now points at; a
// mismatch means a newer deploy exists, so we reload onto it. index.html is
// served must-revalidate, so the reload boots the fresh bundle.
//
// Web is a no-op — isShell() gates everything, so importing this is safe
// everywhere.

const RELOAD_KEY = '_shell_update_reloads'
const MAX_RELOADS = 2 // guard against a pathological reload loop within a session
const CHECK_THROTTLE_MS = 60_000

const isShell = () =>
  !!window.chatroneDesktop || !!window.Capacitor?.isNativePlatform?.()

// The entry script the CURRENTLY-RUNNING document booted from.
function bootedEntry() {
  const s = document.querySelector('script[type="module"][src*="/assets/index-"]')
  return s?.getAttribute('src') || null
}

let _busy = false
let _lastCheck = 0

export async function checkForShellUpdate({ force = false } = {}) {
  if (!isShell() || _busy) return
  const now = Date.now()
  if (!force && now - _lastCheck < CHECK_THROTTLE_MS) return
  _lastCheck = now

  const booted = bootedEntry()
  if (!booted) return

  _busy = true
  try {
    // no-store bypasses the webview cache so we always see the deployed HTML,
    // even when a stale index.html is what the shell booted from.
    const res = await fetch(`${window.location.origin}/?_shellcheck=${now}`, {
      cache: 'no-store',
      credentials: 'omit',
    })
    if (!res.ok) return
    const html = await res.text()
    const m = html.match(/\/assets\/index-[A-Za-z0-9_-]+\.js/)
    const live = m && m[0]
    if (!live) return

    if (live === booted) {
      // Running the current build — clear any prior reload counter so a future
      // update gets its full reload budget.
      try { sessionStorage.removeItem(RELOAD_KEY) } catch { /* private mode */ }
      return
    }

    let n = 0
    try { n = Number(sessionStorage.getItem(RELOAD_KEY) || '0') } catch { /* ignore */ }
    if (n >= MAX_RELOADS) {
      console.warn('[shellUpdate] still stale after reloads — giving up to avoid a loop')
      return
    }
    try { sessionStorage.setItem(RELOAD_KEY, String(n + 1)) } catch { /* ignore */ }
    console.info('[shellUpdate] newer deploy detected — reloading', { booted, live })
    window.location.reload()
  } catch {
    /* offline / transient — retry on the next resume */
  } finally {
    _busy = false
  }
}

// Wire the checker to the moments a shell user could be staring at a stale
// bundle: cold start, and every return-to-foreground. Capacitor's App 'resume'
// is wired separately in nativePlatform.js (more reliable than visibilitychange
// on iOS); this covers desktop/Electron and is a belt-and-suspenders on native.
export function initShellUpdateChecks() {
  if (!isShell()) return
  checkForShellUpdate({ force: true })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForShellUpdate()
  })
  window.addEventListener('focus', () => checkForShellUpdate())
}
